import { Trans, useLingui } from '@lingui/react/macro';
import { ChampionshipMediaKindRow } from 'modules/championship/domain/graphql/championship-media';
import {
  useAssignChampionshipTemplateGroupMutation,
  useChampionshipMediaSettingsQuery,
  useUnassignChampionshipTemplateGroupMutation,
} from 'modules/championship/store/championship-media-api';
import GenerateMediaModal from 'modules/championship/ui/components/form-parts/media/generate-media-modal';
import { MediaKind } from 'modules/manager/domain/media-kind';
import { MediaSubjectType, subjectTypeFor } from 'modules/manager/domain/media-kind/media-kind-catalog';
import { useGenerateMediaMutation } from 'modules/manager/store/media-generation-jobs-api';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { useMediaManagerTemplateGroupsQuery } from 'modules/manager/store/media-manager-api';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFeature } from 'shared/hooks/use-feature';
import { UDSelect, SelectOption } from 'ud-form/components/select/component';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';

interface ChampionshipMediaPageProps {
  championshipId: number;
}

function kindStatusLabel(row: ChampionshipMediaKindRow): { soon: boolean; missing: boolean; name: string | null } {
  const soon = !row.supported || row.blockedReason === 'unsupported';
  const missing = row.blockedReason === 'no_template' || !row.templateName;
  return { soon, missing, name: row.templateName };
}

const ChampionshipMediaPage = ({ championshipId }: ChampionshipMediaPageProps) => {
  const { t } = useLingui();
  const { isEnabled, isFetching: isFeatureFetching } = useFeature('MEDIA_MANAGER');
  const { info } = useOrganizer();
  const tournamentId = info?.id;
  const { data: settings, isFetching } = useChampionshipMediaSettingsQuery(championshipId);
  const { data: groups = [] } = useMediaManagerTemplateGroupsQuery(
    { tournamentId: tournamentId ?? 0 },
    { skip: !tournamentId },
  );
  const [assignGroup, { isLoading: isAssigning }] = useAssignChampionshipTemplateGroupMutation();
  const [unassignGroup, { isLoading: isUnassigning }] = useUnassignChampionshipTemplateGroupMutation();
  const [generateMedia, { isLoading: isGenerating }] = useGenerateMediaMutation();
  const [isChanging, setIsChanging] = useState(false);
  const [picker, setPicker] = useState<{ kind: MediaKind; subjectType: MediaSubjectType; title: string } | null>(null);

  const groupOptions: SelectOption<number>[] = useMemo(
    () => groups.map((group) => ({ value: group.id, label: `${group.name} (${group.templatesCount})` })),
    [groups],
  );

  if (isFeatureFetching) {
    return null;
  }

  if (!isEnabled) {
    return (
      <div className="w-full px-6" data-testid="championship-media-upsell">
        <UDText type="title-3" className="mb-3">
          <Trans>Медиа-менеджер недоступен в вашей подписке</Trans>
        </UDText>
        <UDText type="subhead" className="text-inkSecondary mb-6">
          <Trans>Чтобы привязать пак шаблонов и публиковать картинки автоматически, подключите тариф с медиа-менеджером.</Trans>
        </UDText>
        <Link to={Pages.SETTINGS.builders.subscription('MEDIA_MANAGER')}>
          <UDButton variant="primary" type="button">
            <Trans>Выбрать тариф</Trans>
          </UDButton>
        </Link>
      </div>
    );
  }

  const assigned = settings?.templateGroup;
  const showPicker = !assigned || isChanging;

  const handleAssign = async (option: SelectOption<number> | null) => {
    if (!option) return;
    try {
      await assignGroup({ championshipId, templateGroupId: option.value }).unwrap();
      setIsChanging(false);
    } catch (error) {
      console.error('Failed to assign media template group:', error);
      toast.error(t`Не удалось привязать пак шаблонов`);
    }
  };

  const showGeneratedToast = () => {
    toast.success(
      <span>
        {t`Генерация поставлена в очередь.`}{' '}
        <Link to={Pages.MEDIA_MANAGER.QUEUE} className="underline">
          {t`Открыть очередь`}
        </Link>
      </span>,
    );
  };

  const enqueueGenerate = async (kind: MediaKind, subjectId: number) => {
    try {
      await generateMedia({ championshipId, kind, subjectId }).unwrap();
      showGeneratedToast();
    } catch (error) {
      console.error('Failed to generate media:', error);
      toast.error(t`Не удалось поставить генерацию в очередь`);
    }
  };

  const handleGenerate = (row: ChampionshipMediaKindRow) => {
    const subjectType = (row.subjectType as MediaSubjectType) || subjectTypeFor(row.kind);
    if (subjectType === 'championship') {
      void enqueueGenerate(row.kind, championshipId);
      return;
    }
    setPicker({ kind: row.kind, subjectType, title: row.title });
  };

  const handlePickerSubmit = (subjectId: number) => {
    if (!picker) return;
    const kind = picker.kind;
    setPicker(null);
    void enqueueGenerate(kind, subjectId);
  };

  const handleUnassign = async () => {
    try {
      await unassignGroup(championshipId).unwrap();
      setIsChanging(false);
    } catch (error) {
      console.error('Failed to unassign media template group:', error);
      toast.error(t`Не удалось отвязать пак шаблонов`);
    }
  };

  return (
    <div className="w-full px-6 pb-10" data-testid="championship-media-page">
      <UDText type="title-3" className="mb-2">
        <Trans>Пак шаблонов</Trans>
      </UDText>
      <UDText type="subhead" className="text-inkSecondary mb-6">
        <Trans>Привяжите пак шаблонов, чтобы потом включить автогенерацию картинок по событиям турнира.</Trans>
      </UDText>

      {isFetching && !settings ? (
        <div className="skeleton-block h-24 w-full" data-testid="championship-media-skeleton" />
      ) : assigned && !isChanging ? (
        <div className="border-lineSubtle mb-8 flex items-center justify-between rounded-lg border p-4" data-testid="assigned-group-card">
          <div>
            <UDText type="subhead" className="font-semibold">
              {assigned.name}
            </UDText>
            <UDText type="footnote" className="text-inkMuted">
              {t`${assigned.templatesCount} шаблонов`}
            </UDText>
          </div>
          <div className="flex gap-3">
            <UDButton variant="secondary" type="button" onClick={() => setIsChanging(true)} disabled={isUnassigning}>
              <Trans>Сменить</Trans>
            </UDButton>
            <UDButton variant="secondary" type="button" onClick={() => void handleUnassign()} loading={isUnassigning}>
              <Trans>Отвязать</Trans>
            </UDButton>
          </div>
        </div>
      ) : null}

      {showPicker && (
        <div className="mb-8 max-w-xl" data-testid="championship-media-picker">
          {groupOptions.length === 0 ? (
            <UDText type="subhead" className="text-inkSecondary">
              <Trans>У организации ещё нет паков шаблонов.</Trans>{' '}
              <Link to={Pages.MEDIA_MANAGER.INDEX} className="text-inkPrimary underline">
                <Trans>Открыть медиа-менеджер</Trans>
              </Link>
            </UDText>
          ) : (
            <UDSelect
              placeholder={t`Выберите пак шаблонов`}
              options={groupOptions}
              isDisabled={isAssigning}
              onChange={(value) => void handleAssign(value as SelectOption<number> | null)}
            />
          )}
        </div>
      )}

      {settings && assigned ? (
        <div data-testid="championship-media-kinds">
          <UDText type="title-3" className="mb-4">
            <Trans>Типы медиа</Trans>
          </UDText>
          <ul className="flex flex-col gap-2">
            {settings.kinds.map((row) => {
              const status = kindStatusLabel(row);
              const isSoon = status.soon;
              return (
                <li
                  key={row.kind}
                  className={`flex items-center justify-between rounded-md px-3 py-2 ${isSoon ? 'text-inkMuted' : ''}`}
                  data-testid={`media-kind-row-${row.kind}`}
                >
                  <span className="flex items-center gap-2">
                    <span aria-hidden="true">○</span>
                    <span>{row.title}</span>
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="text-inkMuted text-sm">
                      {isSoon ? t`скоро` : status.missing ? t`нет шаблона` : status.name}
                    </span>
                    {!isSoon && !status.missing ? (
                      <UDButton
                        variant="secondary"
                        type="button"
                        onClick={() => void handleGenerate(row)}
                        loading={isGenerating && picker === null}
                        data-testid={`generate-media-${row.kind}`}
                      >
                        <Trans>Сгенерировать</Trans>
                      </UDButton>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {picker ? (
        <GenerateMediaModal
          championshipId={championshipId}
          kind={picker.kind}
          subjectType={picker.subjectType}
          kindTitle={picker.title}
          onClose={() => setPicker(null)}
          onSubmit={handlePickerSubmit}
        />
      ) : null}
    </div>
  );
};

export default ChampionshipMediaPage;
